import { useState } from "react";
import { History, ShieldCheck } from "lucide-react";

import { useProductProvenance } from "@/api/queries";
import type { FieldProvenance, Product, ValueSource } from "@/api/types";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { StatusBadge, type BadgeTone } from "@/components/StatusBadge";
import { EmptyState } from "@/components/States";
import { formatRelative } from "@/lib/format";
import { formatLength, formatWeight } from "@/lib/units";

/**
 * D'où vient chaque valeur de la fiche (TICKET PM-03).
 *
 * La question à laquelle cette carte répond n'est pas « combien pèse cet
 * article » — la fiche le dit déjà — mais « qui l'a dit, et comment le
 * sait-il ». Devant `2,31 kg`, un magasinier qui prépare une expédition n'a pas
 * la même confiance selon que le chiffre a été lu sur l'étiquette, repris d'une
 * fiche fabricant ou tapé par quelqu'un.
 *
 * Un champ absent d'ici n'a pas de source connue, et c'est une information en
 * soi : personne n'a jamais rien affirmé dessus.
 */

const SOURCES: Record<ValueSource, { label: string; tone: BadgeTone }> = {
  HUMAN: { label: "saisi par un humain", tone: "available" },
  BARCODE: { label: "code-barres décodé", tone: "available" },
  MEASURED: { label: "mesuré", tone: "available" },
  MANUFACTURER: { label: "fiche fabricant", tone: "info" },
  SUPPLIER: { label: "fournisseur", tone: "info" },
  CATALOGUE: { label: "catalogue interne", tone: "info" },
  OCR: { label: "texte lu sur la photo", tone: "info" },
  LOCAL_VISION: { label: "modèle local", tone: "warning" },
  CLOUD_VISION: { label: "modèle cloud", tone: "warning" },
  WEB: { label: "trouvé sur le web", tone: "warning" },
  INFERRED: { label: "déduit, non confirmé", tone: "warning" },
  IMPORT: { label: "import", tone: "unknown" },
  SYSTEM: { label: "valeur système", tone: "unknown" },
};

/** Les noms de champs tels qu'un humain les lit, pas tels que la base les nomme. */
const FIELD_LABELS: Record<string, string> = {
  name: "Nom",
  manufacturer: "Fabricant",
  model: "Modèle",
  part_number: "Numéro de pièce",
  sku: "SKU",
  description: "Description",
  category_id: "Catégorie",
  product_type: "Type",
  tracking_mode: "Mode de suivi",
  stock_unit: "Unité de stock",
  purchase_unit: "Unité d'achat",
  issue_unit: "Unité de sortie",
  net_weight_mg: "Poids net",
  length_mm: "Longueur",
  width_mm: "Largeur",
  height_mm: "Hauteur",
  "package.name": "Conditionnement",
  "package.quantity": "Contenu du conditionnement",
};

function fieldLabel(name: string) {
  return FIELD_LABELS[name] ?? name;
}

/**
 * La valeur telle qu'un humain la lit.
 *
 * La base garde les mesures dans leur unité de base — 2184000 mg, 120 mm — et
 * c'est ce qu'il faut pour calculer. Sur un écran, « 2,184 kg » se relit d'un
 * coup d'œil, et « 120 » tout seul ne veut rien dire.
 */
function fieldValue(name: string, snapshot: string | null) {
  if (snapshot === null) return null;
  if (name === "net_weight_mg") {
    const milligrams = Number(snapshot);
    return Number.isFinite(milligrams) ? formatWeight(milligrams) : snapshot;
  }
  if (name.endsWith("_mm")) return formatLength(snapshot);
  return snapshot;
}

export function ProductProvenanceCard({ product }: { product: Product }) {
  const [showHistory, setShowHistory] = useState(false);
  const { data, isLoading } = useProductProvenance(product.id, {
    includeSuperseded: showHistory,
  });

  const rows = data ?? [];
  const current = rows.filter((row) => row.superseded_at === null);
  const past = rows.filter((row) => row.superseded_at !== null);

  return (
    <Card>
      <CardHeader
        title="Origine des données"
        subtitle="Qui a dit quoi, et comment il le sait."
        action={
          /* Toujours proposé dès qu'il y a une source. Le conditionner à
             l'existence d'une valeur remplacée était sans issue : ces
             valeurs-là ne sont chargées que si l'historique est demandé, donc
             le bouton n'apparaissait jamais et l'historique restait
             inatteignable. */
          current.length > 0 ? (
            <button
              type="button"
              onClick={() => setShowHistory((value) => !value)}
              className="flex items-center gap-1 text-[12px] text-ink-subtle hover:text-ink"
            >
              <History className="size-3.5" aria-hidden />
              {showHistory ? "Masquer l'historique" : "Voir l'historique"}
            </button>
          ) : undefined
        }
      />
      <CardBody className="flex flex-col gap-3">
        {isLoading ? null : current.length === 0 ? (
          <EmptyState
            title="Aucune source enregistrée"
            description="Ce produit a été créé avant le suivi des sources, ou aucun de ses champs n'a encore été renseigné."
          />
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {current.map((entry) => (
              <ProvenanceRow key={entry.id} entry={entry} />
            ))}
          </ul>
        )}

        {showHistory && past.length > 0 && (
          <section>
            <h3 className="mb-1 text-[12px] font-semibold text-ink-subtle">
              Affirmations remplacées
            </h3>
            {/* Rien n'est effacé : « 2200 g d'après le fabricant » puis « 2184 g
                mesuré » sont deux faits vrais, et le second n'annule pas le
                premier — il le date. */}
            <ul className="flex flex-col divide-y divide-line opacity-70">
              {past.map((entry) => (
                <ProvenanceRow key={entry.id} entry={entry} superseded />
              ))}
            </ul>
          </section>
        )}
      </CardBody>
    </Card>
  );
}

function ProvenanceRow({
  entry,
  superseded = false,
}: {
  entry: FieldProvenance;
  superseded?: boolean;
}) {
  const source = SOURCES[entry.source_type] ?? { label: entry.source_type, tone: "unknown" as const };
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2">
      <div className="min-w-0">
        <p className="text-[13px] text-ink">{fieldLabel(entry.field_name)}</p>
        {entry.value_snapshot && (
          <p className="truncate text-[12px] text-ink-subtle">
            {fieldValue(entry.field_name, entry.value_snapshot)}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={source.tone}>{source.label}</StatusBadge>
        {entry.confirmed_by_human && !superseded && (
          <span
            className="flex items-center gap-1 text-[11px] text-ink-subtle"
            title="Un opérateur a confirmé cette valeur."
          >
            <ShieldCheck className="size-3" aria-hidden />
            confirmé
          </span>
        )}
        {/* Le modèle qui a produit la lecture, jamais l'adresse à laquelle il
            répond : un technicien n'a rien à faire d'une IP interne. */}
        {entry.provider_model && (
          <span className="text-[11px] text-ink-subtle">{entry.provider_model}</span>
        )}
        <span className="text-[11px] text-ink-subtle">
          {superseded && entry.superseded_at
            ? `remplacé ${formatRelative(entry.superseded_at)}`
            : formatRelative(entry.created_at)}
        </span>
      </div>
    </li>
  );
}
